import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/context';
import { useTranslation } from 'react-i18next';

const ResetPasswordPage: React.FC = () => {
  const { resetPassword, verifyPasswordReset } = useAuth();
  const { t } = useTranslation('auth');
  const { t: tErr } = useTranslation('errors');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode') || '';
  const returnToParam = searchParams.get('returnTo');
  const continueUrl = searchParams.get('continueUrl');
  const returnTo = useMemo(() => {
    if (returnToParam && returnToParam.startsWith('/')) return returnToParam;
    if (!continueUrl) return '/login';
    try {
      const parsed = new URL(continueUrl);
      return parsed.origin === window.location.origin ? `${parsed.pathname}${parsed.search}` : '/login';
    } catch {
      return continueUrl.startsWith('/') ? continueUrl : '/login';
    }
  }, [continueUrl, returnToParam]);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [linkInvalid, setLinkInvalid] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [resolvedEmail, setResolvedEmail] = useState<string | null>(null);

  useEffect(() => {
    const verify = async () => {
      if (!oobCode) {
        setError(tErr('invalidResetLink'));
        setLinkInvalid(true);
        setIsVerifying(false);
        return;
      }
      try {
        const email = await verifyPasswordReset(oobCode);
        setResolvedEmail(email);
      } catch (err) {
        const message = err instanceof Error ? err.message : tErr('unableToVerifyLink');
        setError(message);
        setLinkInvalid(true);
      } finally {
        setIsVerifying(false);
      }
    };
    void verify();
  }, [oobCode, verifyPasswordReset]);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!password || password.length < 8) {
      setError(tErr('passwordMinLength'));
      return;
    }
    if (password !== confirmPassword) {
      setError(tErr('passwordsDoNotMatch'));
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPassword(oobCode, password);
      setIsComplete(true);
      setTimeout(() => {
        navigate(`${returnTo}${returnTo.includes('?') ? '&' : '?'}recovery=password-reset-success`, { replace: true });
      }, 1600);
    } catch (err) {
      const message = err instanceof Error ? err.message : tErr('resetPasswordFailed');
      setError(message);
      if (message.toLowerCase().includes('invalid or has expired')) setLinkInvalid(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-6 py-12">
      <div className="w-full max-w-md rounded-2xl border border-[#E2E8F0] bg-white p-10 shadow-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-black text-[#0F172A]">{t('chooseNewPassword')}</h1>
          <p className="mt-2 text-sm text-[#64748B]">
            {t('setBrandEdgePassword')}
          </p>
        </div>

        {isVerifying ? (
          <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-4 text-sm text-[#475569]">
            {t('verifyingResetLink')}
          </div>
        ) : isComplete ? (
          <div className="space-y-5">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
              {tErr('passwordUpdated')}
            </div>
            <Link
              to={returnTo}
              className="block h-12 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-4 text-center text-sm font-black leading-[3rem] text-[#0F172A] transition-colors hover:border-[#E10613] hover:text-[#E10613]"
            >
              {t('goToLoginNow')}
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            {resolvedEmail && (
              <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3 text-sm text-[#475569]">
                {t('resetingPasswordFor', { email: resolvedEmail })}
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {error}
              </div>
            )}

            {!linkInvalid && (
              <>
                <div>
                  <label htmlFor="reset-password" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-[#64748B]">{t('newPassword')}</label>
                  <input
                    id="reset-password"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="h-12 w-full rounded-lg border border-[#E2E8F0] bg-white px-4 text-[#0F172A] outline-none transition-colors placeholder:text-[#CBD5E1] focus:border-[#E10613]"
                    placeholder="At least 8 characters"
                  />
                </div>

                <div>
                  <label htmlFor="reset-confirm-password" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-[#64748B]">{t('confirmPassword')}</label>
                  <input
                    id="reset-confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    className="h-12 w-full rounded-lg border border-[#E2E8F0] bg-white px-4 text-[#0F172A] outline-none transition-colors placeholder:text-[#CBD5E1] focus:border-[#E10613]"
                    placeholder="Repeat your new password"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="h-12 w-full rounded-lg bg-[#E10613] font-black text-white transition-colors hover:bg-[#C1121F] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSubmitting ? t('updatingPassword') : t('updatePassword')}
                </button>
              </>
            )}

            <Link
              to={`/forgot-password?returnTo=${encodeURIComponent(returnTo)}`}
              className="block text-center text-sm text-[#64748B] underline underline-offset-4 hover:text-[#0F172A]"
            >
              {t('requestNewResetLink')}
            </Link>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
